/**
 * Report resume pieces (ResumeWorded-style split).
 *   - ResumeDocPane: the user's EXACT uploaded file (the original PDF), clean +
 *     A4-width. Only mounted by the Report when an original file actually exists,
 *     so we never show a "resume" that isn't literally what they uploaded.
 *   - AtsReadCard: "what the ATS reads" — sections + presence. PURE render from a
 *     `parsed` object the Report already fetched (no own network call).
 * Both fail quiet (render nothing) so they can never break the page.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FileText, Bot, Check, X, Lightbulb, Loader2, AlertTriangle, Maximize2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PANE_HEIGHT = { height: "calc(100vh - 9.5rem)", minHeight: 520 };

const STANDARD_SECTIONS = [
  { key: "summary", label: "Summary" },
  { key: "experience", label: "Experience" },
  { key: "education", label: "Education" },
  { key: "skills", label: "Skills" },
  { key: "projects", label: "Projects" },
  { key: "certifications", label: "Certifications" },
];

// ── LEFT: the exact uploaded file (original PDF), clean + A4-width ────────────
export function ResumeDocPane({ resumeId, getHeaders, onReady }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [state, setState] = useState("loading"); // loading | pdf | failed
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!resumeId) { setState("failed"); onReady?.(); return undefined; }
    (async () => {
      try {
        const f = await axios.get(`${API}/resume/${resumeId}/file`, { headers: getHeaders(), responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(f.data);
        urlRef.current = url; setPdfUrl(url); setState("pdf");
      } catch { if (!cancelled) setState("failed"); }
      // Signal the parent either way so it can reveal the page as one unit
      // (report + resume together) instead of the pane popping in late.
      if (!cancelled) onReady?.();
    })();
    return () => { cancelled = true; if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; } };
  }, [resumeId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === "failed") return null; // parent only mounts this when a file exists

  return (
    <div className="ap-card overflow-hidden p-0" data-testid="resume-doc-pane">
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: "1px solid var(--ap-line)", background: "#fafbff" }}>
        <FileText className="w-4 h-4" style={{ color: "var(--ap-violet)" }} />
        <span className="text-[13px] font-bold text-[var(--ap-ink)]">Your resume</span>
        <span className="text-[11px] hidden sm:inline" style={{ color: "var(--ap-green)" }}>&nbsp;· your exact upload</span>
        {state === "pdf" && (
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-[11px] ml-auto inline-flex items-center gap-1 font-semibold" style={{ color: "var(--ap-violet)" }}>
            <Maximize2 className="w-3 h-3" /> Open full size
          </a>
        )}
      </div>
      {state === "loading" ? (
        <div className="flex items-center justify-center bg-[#eef0f4]" style={PANE_HEIGHT}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--ap-violet)" }} />
        </div>
      ) : (
        // toolbar=0 & navpanes=0 strip the browser PDF chrome (thumbnails, zoom/
        // print/download) so it shows just the page, clean, at A4 width.
        <iframe src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title="Your resume" className="w-full bg-[#eef0f4]" style={{ ...PANE_HEIGHT, border: 0 }} data-testid="resume-doc-pdf" />
      )}
    </div>
  );
}

// ── RIGHT: what an ATS reads (pure render from the Report's parsed data) ─────
export function AtsReadCard({ parsed }) {
  if (!parsed) return null;

  const presence = parsed.presence || {};
  const contact = parsed.contact || {};
  const hasContact = Boolean(contact.email || contact.phone);
  const detected = STANDARD_SECTIONS.filter((s) => presence[s.key]).length;
  const missing = STANDARD_SECTIONS.filter((s) => !presence[s.key]);
  const emptyResume = (parsed.text_chars || 0) < 40;
  const contactBits = [parsed.name && "name", contact.email && "email", contact.phone && "phone"].filter(Boolean);
  const firstMissingIdx = STANDARD_SECTIONS.findIndex((x) => !presence[x.key]);

  const Row = ({ ok, label, detail }) => (
    <div className="flex items-center gap-2.5 py-[7px]" style={{ borderBottom: "1px solid #F0EFEA" }}>
      {ok ? <Check className="w-4 h-4 shrink-0" style={{ color: "var(--ap-green)" }} /> : <X className="w-4 h-4 shrink-0" style={{ color: "#c2410c" }} />}
      <span className="text-[13.5px]" style={{ color: ok ? "var(--ap-ink)" : "#c2410c", fontWeight: ok ? 400 : 600 }}>{label}</span>
      {detail && <span className="text-[11px] ml-auto text-right" style={{ color: ok ? "var(--ap-muted)" : "#c2410c" }}>{detail}</span>}
    </div>
  );

  return (
    <div className="ap-card ap-rise p-5" data-testid="ats-read-card">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "var(--ap-violet-soft)" }}>
          <Bot className="w-4 h-4" style={{ color: "var(--ap-violet)" }} />
        </div>
        <span className="ap-eyebrow">What the ATS reads</span>
      </div>
      <p className="text-[var(--ap-muted)] text-[12.5px] mb-3 ml-10">An automated parse of your file — the sections a screening system can actually see.</p>
      {emptyResume ? (
        <div className="flex flex-col items-center justify-center text-center px-4 py-10">
          <AlertTriangle className="w-7 h-7 mb-2" style={{ color: "#d97706" }} />
          <p className="text-[14px] font-bold text-[var(--ap-ink)] m-0">We couldn't read any text from this file</p>
          <p className="text-[12.5px] text-[var(--ap-muted)] mt-1.5 max-w-[320px]">It's likely a scanned image or photo resume — which ATS systems also reject. Re-upload a text-based PDF.</p>
        </div>
      ) : (
        <>
          <Row ok={hasContact} label="Contact" detail={hasContact ? contactBits.join(" · ") : "no email/phone found"} />
          {STANDARD_SECTIONS.map((s, i) => (
            <Row key={s.key} ok={!!presence[s.key]} label={s.label}
              detail={presence[s.key] ? "detected" : (i === firstMissingIdx ? "not found — add to rank higher" : "not found")} />
          ))}
          <div className="rounded-xl px-4 py-3 mt-4 flex items-start gap-2.5" style={{ background: "linear-gradient(120deg,#F3F1FE,#FBEFF4)", border: "1px solid #ECEAF6" }} data-testid="ats-read-diagnosis">
            <Lightbulb className="w-[18px] h-[18px] shrink-0 mt-0.5" style={{ color: "var(--ap-violet)" }} />
            <p className="text-[13px] text-[var(--ap-ink-2)] m-0">
              <b>An ATS cleanly read {detected} of {STANDARD_SECTIONS.length} standard sections.</b>
              {missing.length > 0
                ? ` It couldn't find ${missing.map((m) => m.label).join(", ")} — adding ${missing.length === 1 ? "it" : "them"} will lift your score.`
                : " Your structure is clean and ATS-readable."}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
